export type AuthStack = "html" | "react-vite" | "astro";

export interface AuthCombination {
  integrationCode: string;
  lang: string;
  claudePrompt: string;
}

const PLATFORM_URL = "https://apps.jensen-dowell.me";

const SUFFIX =
  "After completing, add the dg-connectors.json entry shown in the brief. Confirm each step is complete before moving to the next.";

export const PLATFORM_AUTH: Record<AuthStack, AuthCombination> = {
  html: {
    lang: "javascript",
    integrationCode: `// Place at top of main.js — before any content renders
(async function () {
  document.body.style.visibility = 'hidden'
  const platform = document.querySelector('meta[name="dg-platform-url"]')
    ?.content ?? '${PLATFORM_URL}'
  try {
    const res = await fetch(\`\${platform}/api/v1/session\`, { credentials: 'include' })
    if (res.status === 401) {
      window.location.href = \`\${platform}/?redirect=\${encodeURIComponent(window.location.href)}\`
      return
    }
    const { user } = await res.json()
    window.__DG_USER__ = user
    document.body.style.visibility = 'visible'
  } catch {
    document.body.style.visibility = 'visible'
    // Handle network error — show error state
  }
})()`,
    claudePrompt: `Read the attached brief in full. Integrate the Platform Auth connector into this HTML/CSS/JS app. Add the session check at the top of main.js before any content renders. The platform is at ${PLATFORM_URL}. Add the integration code exactly as shown in the brief. ${SUFFIX}`,
  },
  "react-vite": {
    lang: "tsx",
    integrationCode: `// src/hooks/useAuth.ts
import { useEffect, useState } from 'react'

const PLATFORM = import.meta.env.VITE_DG_PLATFORM_URL ?? '${PLATFORM_URL}'

export function useAuth() {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch(\`\${PLATFORM}/api/v1/session\`, { credentials: 'include' })
      .then(res => {
        if (res.status === 401) {
          window.location.href = \`\${PLATFORM}/?redirect=\${encodeURIComponent(window.location.href)}\`
          return
        }
        return res.json()
      })
      .then(data => { if (data) setUser(data.user) })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  return { user, loading }
}`,
    claudePrompt: `Read the attached brief in full. Integrate the Platform Auth connector into this React + Vite app. Create src/hooks/useAuth.ts and use it to gate content behind authentication. Set VITE_DG_PLATFORM_URL in .env. Add the integration code exactly as shown in the brief. ${SUFFIX}`,
  },
  astro: {
    lang: "astro",
    integrationCode: `<!-- src/pages/index.astro or src/layouts/Layout.astro -->
<script>
  const platform = document.querySelector('meta[name="dg-platform-url"]')
    ?.content ?? '${PLATFORM_URL}'
  fetch(\`\${platform}/api/v1/session\`, { credentials: 'include' })
    .then(r => r.status === 401
      ? (window.location.href = \`\${platform}/?redirect=\${encodeURIComponent(window.location.href)}\`)
      : r.json())
    .catch(() => {})
</script>`,
    claudePrompt: `Read the attached brief in full. Integrate the Platform Auth connector into this Astro app. Add the session check script to src/pages/index.astro or src/layouts/Layout.astro. The platform is at ${PLATFORM_URL}. Add the integration code exactly as shown in the brief. ${SUFFIX}`,
  },
};
